<template>
  <wat-sew-collisions-info
    :title-straight="joinsSettingMapped?.name"
    :custom-collisions-trees="featuresTree"
    :layer-id="layerId"
    enable-zoom-highlight
    hover-color="255,0,0"
    :tabs="tabs"
    hide-buffer
  >
    <template #prepend-tabs>
      <v-row no-gutters class="my-3">
        <v-col>
          <v-menu
            bottom
            offset-y
            v-model="isAssignMultipleMenuVisible"
            transition="scroll-y-transition"
            :close-on-click="false"
            @close="() => (multipleAssignItems = [])"
            min-width="280"
            max-width="400"
            z-index="1"
          >
            <template #activator="{}">
              <data-input
                simple
                ref="feature-to-add-input-joins"
                :data-type="{ name: 'features' }"
                :featuresSettings="featuresSettings"
                translation-path="sidebar.joins.addFeatureFromMap"
                subtitle-translation-path="sidebar.joins.addFeatureFromMapSubtitle"
                @pushFeatures="handleSelectFeature"
                @status="handleAssignButtonStatus"
              />
            </template>
            <v-list v-if="multipleAssignItems.length">
              <v-list-item
                v-for="(feature, index) of multipleAssignItems"
                @click="openAssignFeatureModal(feature.ftData)"
                :key="index"
              >
                <template>
                  <v-list-item-content>
                    <v-tooltip bottom color="font" max-width="300" :disabled="feature.title.length <= 45">
                      <template #activator="{ on }">
                        <v-list-item-title class="text-left" v-on="on">{{ feature.title }}</v-list-item-title>
                      </template>
                      <div>
                        {{ feature.title }}
                      </div>
                    </v-tooltip>
                  </v-list-item-content>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
      <main-dialog
        :v-if="isAssignFeatureDialogLoaded"
        :is-visible.sync="isAssignFeatureDialogVisible"
        :loading="isAssignFeatureDialogLoading"
        :title="$i18n.t('sidebar.joins.assignTitle')"
        :body="$i18n.t('sidebar.joins.assignBody', currentAssignFeatureData.translationData)"
        @agree="handleEditFeature"
        agree-text="dialog.agreeJoin"
      />
    </template>
  </wat-sew-collisions-info>
</template>
<script>
import { call, get } from 'vuex-pathify';
import WatSewCollisionsInfo from '@/components/WatSewCollisionsInfo';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'FeatureJoins',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    WatSewCollisionsInfo,
    DataInput,
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    layerId: {
      type: Number,
      required: true,
    },
    featureId: {
      type: Number,
      required: true,
    },
    values: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    featuresSettings() {
      return {
        layers: {
          included: [this.joinsSettingMapped['relationLayer']],
        },
      };
    },
    relationDataSourceIdAttributeName() {
      return this.dataSources[this.layers[this.joinsSettingMapped?.relationLayer].data_source_name]?.attributes_schema
        .id_name;
    },
    relationDataSourceDescAttributeName() {
      return this.dataSources[this.layers[this.joinsSettingMapped?.relationLayer].data_source_name]?.attributes_schema
        .desc_attribute_name;
    },
    relationLayerAttributes() {
      return this.$_flattenStructure(
        this.getForm(
          this.layers[this.joinsSettingMapped?.relationLayer]?.form_schema?.elements || [],
          this.dataSources[this.layers[this.joinsSettingMapped?.relationLayer].data_source_name].attributes_schema
            ?.attributes || []
        )
      );
    },
    joinsSettings: get('joins/joins'),
    joinsSettingsArray() {
      return this.$_objectToArray(this.joinsSettings);
    },
    joinsSetting() {
      return this.joinsSettingsArray?.find(join => join.main_layer === this.layerId) || undefined;
    },
    joinsSettingMapped() {
      return {
        name: this.joinsSetting?.['name'],
        mainLayerAttribute: this.joinsSetting?.['main_key_attribute'],
        relationLayer: this.joinsSetting?.['join_layer'],
        relationLayerAttribute: this.joinsSetting?.['join_key_attribute'],
      };
    },
    tabs() {
      return [
        {
          value: 'basic',
          moduleName: 'JOINS',
          settingName: 'joins_module_enabled',
          scope: 'basic',
        },
      ];
    },
    featuresTree() {
      if (!this.joinsSettingMapped?.relationLayer || !this.features.length) return;
      return {
        basic: [
          {
            id: this.joinsSettingMapped?.relationLayer,
            name: this.layers[this.joinsSettingMapped?.relationLayer]?.name,
            children: this.features,
          },
        ],
      };
    },
  },
  data: () => ({
    features: [],
    isAssignFeatureDialogLoaded: false,
    isAssignFeatureDialogVisible: false,
    isAssignFeatureDialogLoading: false,
    currentAssignFeatureData: {},
    multipleAssignItems: [],
    isAssignMultipleMenuVisible: false,
  }),
  methods: {
    editDatasourceFeature: call('layers/editDatasourceFeature'),
    getLayerFeatures: call('layers/getLayerFeatures'),
    getJoinsSettings: call('joins/getJoins'),
    getJoinsFilter({ mainLayerAttribute, relationLayer, relationLayerAttribute } = this.joinsSettingMapped) {
      const dS = this.dataSources[this.layers[relationLayer]?.data_source_name];
      return {
        $IN: {
          [dS.attributes_use_datasource_qualified_names
            ? relationLayerAttribute
            : `${dS.name}.${relationLayerAttribute}`]: [this.values[mainLayerAttribute]],
        },
      };
    },
    assignFeatureData(featureData) {
      this.currentAssignFeatureData = {
        relationFeatureId: featureData.id,
        translationData: {
          relation_feature_desc_name: featureData.descAttributeValue,
          relation_feature_layer: this.layers[featureData.layerId]?.name,
          main_feature_desc_name: this.values[
            this.dataSources[this.layers[this.joinsSetting.main_layer]?.data_source_name]?.attributes_schema
              .desc_attribute_name
          ],
          main_feature_layer: this.layers[this.joinsSetting.main_layer]?.name,
        },
      };
    },
    openAssignFeatureModal(featureData) {
      this.assignFeatureData(featureData);
      this.isAssignFeatureDialogLoaded = this.isAssignFeatureDialogVisible = true;
    },
    handleAssignButtonStatus(status) {
      if (status) return;
      this.isAssignMultipleMenuVisible = false;
      this.multipleAssignItems = [];
    },
    handleSelectFeature(pointedFeatures) {
      if (!pointedFeatures.length) return;
      const currentlyJoinedFeaturesIds = this.features.map(ft => ft.featureId);
      const filteredPointedFeatures = pointedFeatures.filter(ft => !currentlyJoinedFeaturesIds.includes(ft.id));
      if (!filteredPointedFeatures.length) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t(
            pointedFeatures.length > 1 ? 'snackbar.featuresAreAlreadyJoined' : 'snackbar.featureIsAlreadyJoined'
          ),
        });
        return;
      }
      this.isAssignMultipleMenuVisible = false;
      if (filteredPointedFeatures.length === 1) {
        this.openAssignFeatureModal(filteredPointedFeatures[0]);
      } else {
        this.multipleAssignItems = filteredPointedFeatures.map(ft => ({
          ftData: ft,
          title: ft.descAttributeValue,
        }));
        this.isAssignMultipleMenuVisible = true;
      }
    },
    async handleEditFeature() {
      this.isAssignFeatureDialogLoading = true;
      try {
        await this.editDatasourceFeature({
          datasource: this.layers[this.joinsSettingMapped?.relationLayer]?.data_source_name,
          id: this.currentAssignFeatureData.relationFeatureId,
          body: {
            [this.joinsSettingMapped.relationLayerAttribute]: this.values[this.joinsSettingMapped.mainLayerAttribute],
          },
          params: { layer_id: this.joinsSettingMapped?.relationLayer },
        });
        await this.init();
      } finally {
        this.$refs[`feature-to-add-input-joins`]?.$refs['data-input']?.toggleOff();
        this.isAssignFeatureDialogLoading = this.isAssignFeatureDialogVisible = this.isAssignMultipleMenuVisible = false;
      }
    },
    async init() {
      this.isFetched = false;
      if (!this.joinsSettingsArray?.length || !this.joinsSetting) {
        await this.getJoinsSettings();
      }
      if (!this.joinsSetting) {
        this.isFetched = true;
        return;
      }
      const features_filter = this.getJoinsFilter();
      const payload = {
        layer_id: this.joinsSettingMapped['relationLayer'],
        features_filter,
        params: {
          with_features: true,
          with_collection_bbox: false,
          with_count: false,
          with_geometry: true,
        },
      };
      const r = await this.getLayerFeatures(payload);
      const relationLayerDescAttributeVerboseName = this.relationLayerAttributes.find(
        attribute => attribute.attribute === this.relationDataSourceDescAttributeName
      )?.label;
      this.features = r.data.data.features.features.map(feature => {
        return {
          featureId: feature.id,
          id: `${this.joinsSetting?.relationLayer}-${feature.id}`,
          geometry: { ...feature.geometry, crs: r.data.data.features.crs },
          name: `${relationLayerDescAttributeVerboseName}: ${
            { ...feature.properties, [this.relationDataSourceIdAttributeName]: feature.id }[
              this.relationDataSourceDescAttributeName
            ]
          }`,
        };
      });
      this.isFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>
